import React, { memo, useCallback, useContext, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import cx from "classnames";
import { Row, Col, DatePicker } from "antd";
import { RangePickerValue } from "antd/es/date-picker/interface";
import { useTranslation } from "react-i18next";
import qs from "query-string";
import { UserViewContext } from "app/store/contexts/userContextView";
import Permission from "app/components/Permission/Permission";
import { PermissionEnum } from "app/constants/permissionConst";
import {
  DateFormats,
  PeriodsConst,
  PeriodsEnum,
} from "app/constants/dateConst";
import Select, { Option } from "app/components/Select/Select";
import styles from "./PageFilter.module.scss";
import { getPeriodFromToday } from "app/helpers/utilHelper";
import ErrorFallbackUI from "app/components/ErrorFallbackUI";
import { ReaderType, MeasurementMethod } from "@generated/v2";
import _camelCase from "lodash/camelCase";
// Test Records Filters
import TestResultFilter from "app/screens/TestRecordsScreen/components/TestResultFilter/TestResultFilter";

// Control Records Filters
import AnnotationFilter from "app/screens/ControlRecordsScreen/AnnotationFilter/AnnotationFilterFilter";

import { readerTypes } from "app/constants/deviceConst";
// import { useInfo } from "app/state/userInfo";
import { useTestRecords } from "app/state/testRecords";
import { cloneDeep } from "lodash";
import { PRIVATE_ROUTES } from "app/routers/Router.config";
import {
  DEFAULT_TEST_RESULTS,
  DEFAULT_CONTROL_RESULTS,
} from "app/constants/filterConst";

const { RangePicker } = DatePicker;

interface Props {
  deviceFilter?: boolean;
  siteFilter?: boolean;
  dateFilter?: boolean;
  /** display routes filter */
  routesFilter?: boolean;
  /** display test results filter */
  testResultFilter?: boolean;
  /** display control results filter */
  annotationFilter?: boolean;
  /** display measurement method filter */
  measurementMethodFilter?: boolean;
  usersFilter?: boolean;
  /**
   * Used for filtering on the users screen.
   * NOT TO BE CONFUSED WITH usersFilter.
   */
  userFilter?: boolean;
  /**
   * Extra content to be outputted on the
   * right side of the page filter
   */
  extra?: React.ReactNode;
  /** Filter Input Box Width */
  lg?: number;
  xl?: number;
}

const PageFilter = ({
  deviceFilter,
  siteFilter,
  dateFilter,
  routesFilter,
  testResultFilter,
  annotationFilter,
  usersFilter,
  userFilter,
  measurementMethodFilter,
  extra,
  xl = 6,
  lg = 12,
}: Props) => {
  const { t } = useTranslation();
  // const { filters } = useInfo();
  const { views } = useTestRecords();

  const location = useLocation();
  const history = useHistory();

  const currentSearch = qs.parse(location.search);
  const viewId = Number(currentSearch?.viewId);

  const { userInfoView, setUserInfoView } = useContext(UserViewContext);
  const isUserViews =
    location.pathname === PRIVATE_ROUTES.TEST_RECORD_USER_VIEWS_SCREEN.path;

  const {
    period,
    dates,
    testResult,
    controlResult,
    readerType,
    measurementMethod,
  } = userInfoView?.filters;

  /**
   * Remove page parameter from query string, as the data set will have changed,
   * due to selection of new dates.
   */
  const resetPage = useCallback(() => {
    const currentSearch = qs.parse(location.search);

    if (!!currentSearch.page) {
      delete currentSearch.page;

      history.replace({
        pathname: location.pathname,
        search: qs.stringify(currentSearch, {
          arrayFormat: "comma",
        }),
      });
    }
  }, [history, location.pathname, location.search]);

  const handlePeriodSelection = (period: PeriodsEnum) => {
    resetPage();
    if (!!period) {
      const type = period.toLowerCase().includes("days") ? "days" : "months";
      setUserInfoView({
        filters: {
          ...userInfoView.filters,
          period,
          dates: getPeriodFromToday(PeriodsConst[period], type),
        },
      });
    } else {
      setUserInfoView({
        filters: {
          ...userInfoView.filters,
          period: PeriodsEnum.MONTHS_6,
          dates: getPeriodFromToday(),
        },
      });
    }
  };

  const handleDateSelection = (dates: RangePickerValue) => {
    resetPage();
    if (dates.length > 0) {
      setUserInfoView({
        filters: { ...userInfoView.filters, dates: dates, period: undefined },
      });
    } else {
      setUserInfoView({
        filters: {
          ...userInfoView.filters,
          dates: getPeriodFromToday(),
          period: PeriodsEnum.MONTHS_6,
        },
      });
    }
  };

  const handleDeviceTypeSelect = useCallback(
    (value: ReaderType) => {
      resetPage();
      setUserInfoView({
        filters: {
          ...userInfoView.filters,
          readerType: value,
        },
      });
    },
    [resetPage, setUserInfoView, userInfoView.filters]
  );

  /**
   * Test result filter onChange handler
   *
   * @param {number} weight new test results filters weight
   */
  const handleTestResultFilterChange = (weight: number) => {
    resetPage();
    setUserInfoView({
      filters: {
        ...userInfoView.filters,
        testResult: weight,
      },
    });
  };

  /**
   * Control result filter onChange handler
   *
   * @param {number} weight new Control results filters weight
   */
  const handleAnnotationFilterChange = (weight: number) => {
    resetPage();
    setUserInfoView({
      filters: {
        ...userInfoView.filters,
        controlResult: weight,
      },
    });
  };

  const handleMeasurementMethod = useCallback(
    (value: MeasurementMethod) => {
      setUserInfoView({
        filters: {
          ...userInfoView.filters,
          measurementMethod: value,
        },
      });
    },
    [setUserInfoView, userInfoView.filters]
  );

  const getFiltersItem = useCallback(() => {
    const view = views?.views?.find(view => view.id === viewId);
    const filters = cloneDeep(view?.filters);
    const period = view?.filters?.period;
    const type = period
      ? period.toLowerCase().includes("days")
        ? "days"
        : "months"
      : "months";

    if (filters) {
      filters.dates = getPeriodFromToday(PeriodsConst[period], type);

      setUserInfoView({
        filters: {
          ...filters,
        },
      });
    }
  }, [setUserInfoView, viewId, views]);

  useEffect(() => {
    if (viewId) {
      getFiltersItem();
    }
    if (!viewId && isUserViews) {
      setUserInfoView({
        filters: {
          period: PeriodsEnum.MONTHS_6,
          routes: [] as string[],
          testResult: DEFAULT_TEST_RESULTS,
          controlResult: DEFAULT_CONTROL_RESULTS,
          userIds: [],
        },
      });
    }
  }, [getFiltersItem, isUserViews, setUserInfoView, viewId, views]);

  return (
    <ErrorFallbackUI>
      <Row gutter={16}>
        {measurementMethodFilter && (
          <Col span={24} lg={12} xl={xl}>
            <Select
              className={styles.select}
              placeholder={t("pageFilter.measurementMethod")}
              size="large"
              onChange={value => handleMeasurementMethod(value)}
              value={measurementMethod}
              allowClear
            >
              {Object.keys(MeasurementMethod).map(key => (
                <Option
                  key={MeasurementMethod[key]}
                  value={MeasurementMethod[key].toString()}
                >
                  {t(`measurementMethod.${_camelCase(key)}`)}
                </Option>
              ))}
            </Select>
          </Col>
        )}
        {deviceFilter && (
          <Permission
            requiredPermissions={[
              PermissionEnum.ACCOUNTS_CUSTOMER_READ,
              PermissionEnum.ACCOUNTS_SITE_READ,
            ]}
          >
            <Col span={24} lg={12} xl={xl}>
              <Select
                placeholder={t("pageFilter.deviceType")}
                className={styles.select}
                onChange={handleDeviceTypeSelect}
                value={readerType}
              >
                {readerTypes.map(device => (
                  <Option key={device.key} value={device.key}>
                    {device.title}
                  </Option>
                ))}
              </Select>
            </Col>
          </Permission>
        )}

        {dateFilter && (
          <>
            <Col span={24} lg={lg} xl={xl}>
              <Select
                className={styles.select}
                placeholder={t("pageFilter.selectPeriod")}
                onChange={handlePeriodSelection}
                value={period}
              >
                {Object.keys(PeriodsEnum).map(key => (
                  <Option key={PeriodsEnum[key]} value={PeriodsEnum[key]}>
                    {t(`pageFilter.${PeriodsEnum[key]}`)}
                  </Option>
                ))}
              </Select>
            </Col>
            {!isUserViews && (
              <Col span={24} lg={lg} xl={xl}>
                <RangePicker
                  value={dates}
                  format={DateFormats.DATE_DMY_NO_HYPHEN}
                  separator="-"
                  size="large"
                  onChange={handleDateSelection}
                  className={styles.select}
                />
              </Col>
            )}
          </>
        )}
        {testResultFilter && (
          <Col span={24} lg={12} xl={xl}>
            <TestResultFilter
              // disabled={!customerId}
              testResult={testResult!}
              onChange={handleTestResultFilterChange}
            />
          </Col>
        )}
        {annotationFilter && (
          <Col span={24} lg={12} xl={xl}>
            <AnnotationFilter
              // disabled={!customerId}
              controlResult={controlResult!}
              onChange={handleAnnotationFilterChange}
            />
          </Col>
        )}
        {extra && (
          <Col
            className={cx({ [styles.extra]: !dateFilter })}
            span={24}
            lg={lg}
            xl={dateFilter ? 6 : 12}
          >
            <div className={styles.extraContent}>{extra}</div>
          </Col>
        )}
      </Row>
    </ErrorFallbackUI>
  );
};

export default memo(PageFilter);
